import { Filter } from "@/shared/lib/filter/filter";
import { MonitorRecord } from "@/services/backend/qpm/monitors/types/monitor";
import { Area } from "@/services/backend/qpm/buildings/types/building";
import {
  MonitorType,
  RiskLevelKey,
  RiskTypeKey,
} from "@/services/backend/qpm/types/enums";
import { Address } from "@/services/backend/addresses/types/address";
import {
  DocumentBase,
  DocumentImmutability,
} from "@/services/backend/qpm/types/document";
import { ProcessArtefact } from "@/services/backend/qpm/types/process-artefact";
import { RequirementDTO } from "@/services/backend/qpm/requirements/types/requirement-dto";

export type Requirement = RequirementData &
  DocumentBase &
  DocumentImmutability & {
    processArtefact?: ProcessArtefact;
    monitors: Record<string, MonitorRecord>;
    postalAddress: Address;
    serviceAddress: Address;
    subject: string;
    description: string;
  };

export interface RequirementListDTO extends DocumentBase {
  id: string;
  subject: string;
  description: string;
  customerId: string;
  createdAt: string;
  updatedAt: string;
  immutable: boolean;
}

export type RequirementData = {
  areaList: RequirementArea[];
  selectedMonitors: SelectedMonitor[];
  monitorSummary: SelectedMonitorSummary;
  pestTypeList: RequirementPestList;
  pestCategoryList: RequirementPestCategoryList;
  additionalInformation: RequirementAdditionalInformation;
};

export type SelectedMonitor = {
  id: string;
  areaId: string;
  monitorId: string;
  monitorTypeKey: MonitorType;
  includeInCheckpointService?: boolean;
  hasLethalAgent: boolean;
  amount: number;
  amountOfMeasures: number;
  existingStockAmount: number;
  comment: string;
  position: string;
};

export type SelectedAreaMonitors = {
  monitors: SelectedMonitor[];
};

export type MonitorSummary = {
  monitorId: string;
  monitorType: MonitorType;
  hasLethalAgent: boolean;
  totalAmount: number;
  totalInstallationTime: number;
  totalServiceTime: number;
  basePrice: number;
  totalPrice: number;
};

export type SelectedMonitorSummary = {
  requirementId: string;
  monitorSummaryList: MonitorSummaryList;
  typeSummary: { [key in MonitorType]: number };
  totalInstallationTime: number;
  totalServiceTime: number;
};

export type MonitorSummaryList = {
  monitors: MonitorSummary[];
};

export type CreateRequirementRequest = {
  requirementId: string;
  customerId: string;
  processId: string;
  serviceObjectId: string;
  companyId: string;
  branchId: string;
  catalogId?: string;
  subject?: string;
  description?: string;
};

export type PutRequirementRequest = {
  id: string;
  processId?: string;
  requirement: RequirementDTO;
};

export type GetRequirementRequest = {
  id: string;
};

export type RequirementList = {
  items: RequirementListDTO[];
  total: number;
  limit: number;
  offset: number;
};

export type ListRequirementRequest = {
  process: Filter<string>;
  createdAt: Filter<string>;
  updatedAt: Filter<string>;
  company: Filter<string>;
  branch: Filter<string>;
  customer: Filter<string>;
  serviceObject: Filter<string>;
  orderBy: { field: string; dsc: boolean }[];
  limit: number;
};

export type DeleteRequest = {
  id: string;
};

export type RequirementArea = {
  areaId: string;
  amountOfMeasures: number;
  area: Area;
  risks: RequirementAreaRisk[];
  createdAt: string;
};

export type RequirementPest = {
  id: string;
  pestId: string;
  pestPresent: boolean;
  pestType: string;
  pestName: string;
  pestDescription: string;
};

export type RequirementPestList = { pests: RequirementPest[] };

export type RequirementPestCategory = {
  id: string;
  pestCategoryId: string;
  pestPresent: boolean;
  position: boolean;
  type: string;
  name: string;
  description: string;
  requiredQpmCyclePerYear: number;
  immutable: boolean;
};

export type RequirementPestCategoryList = {
  pestCategories: RequirementPestCategory[];
};

export enum RequirementCycleType {
  None = "",
  InitialControl = "initial-control",
  InitialControlEradicationMonitoring = "initial-control-eradication-monitoring",
}

export type RequirementAdditionalInformation = {
  certificateNotes: string;
  additionalNotes: string;
  travelTime: number;
  travelCompany: string;
  cycleType: RequirementCycleType;
  qpmCycleAmountPerYear: number;
  aloraAmountOfMeasures: number;
  scbAmountOfMeasures: number;
  basicRefurbishmentMonthlyHours: number;
  redemptionControlTimeAfterWeeks: number;
  redemptionControlMonthlyHours: number;
  installationTime: number;
  maintenanceTime: number;
  rfidServiceTime: number;
  hygieneAuditTimesPerYear: number;
  hygieneAuditMonthlyHours: number;
};

export type RequirementAreaRisk = {
  areaId: string;
  riskTypeKey: RiskTypeKey;
  riskLevelKey: RiskLevelKey;
  detection: string;
  recommendation: string;
  measure: string;
};

export function newRequirementAreaRisks(areaId: string): RequirementAreaRisk[] {
  return [
    newRequirementAreaRisk(areaId, RiskTypeKey.CrawlingInsect),
    newRequirementAreaRisk(areaId, RiskTypeKey.FlyingInsect),
    newRequirementAreaRisk(areaId, RiskTypeKey.Rodent),
    newRequirementAreaRisk(areaId, RiskTypeKey.Bird),
  ];
}

export function newRequirementAreaRisk(
  areaId: string,
  riskTypeKey: RiskTypeKey,
): RequirementAreaRisk {
  return {
    areaId,
    riskTypeKey,
    riskLevelKey: RiskLevelKey.Unspecified,
    detection: "",
    recommendation: "",
    measure: "",
  };
}
